// EventBus.ts

export type Handler<T = unknown> = (data: T) => void;

export class Emitter<Events extends Record<string, any> = Record<string, unknown>> {
  // Map of event name -> set of handlers
  private map = new Map<keyof Events & string, Set<Handler>>();

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<K extends keyof Events & string>(type: K, fn: Handler<Events[K]>): () => void {
    if (!this.map.has(type)) this.map.set(type, new Set());
    this.map.get(type)!.add(fn as Handler);
    return () => this.off(type, fn);
  }

  /** Remove a specific handler for an event. */
  off<K extends keyof Events & string>(type: K, fn: Handler<Events[K]>): void {
    this.map.get(type)?.delete(fn as Handler);
  }

  /** Emit an event with a payload (sync). */
  emit<K extends keyof Events & string>(type: K, data: Events[K]): void {
    const set = this.map.get(type);
    if (!set || set.size === 0) return;
    // Copy to prevent mutation during iteration
    [...set].forEach((fn) => fn(data));
  }

  /** Subscribe once: auto-unsubscribe after first call. */
  once<K extends keyof Events & string>(type: K, fn: Handler<Events[K]>): () => void {
    const off = this.on(type, (payload) => {
      off();
      fn(payload);
    });
    return off;
  }

  /** Remove all listeners; if `type` provided, only clear that event. */
  clear(type?: keyof Events & string): void {
    if (type) this.map.get(type)?.clear();
    else this.map.clear();
  }

  /** Number of listeners for an event. */
  listenerCount(type: keyof Events & string): number {
    return this.map.get(type)?.size ?? 0;
  }

  /** Promise that resolves on the next occurrence of `type`. */
  waitFor<K extends keyof Events & string>(type: K): Promise<Events[K]> {
    return new Promise<Events[K]>((resolve) => {
      this.once(type, resolve);
    });
  }
}

/** Factory to create a new Emitter instance (keeps your existing API). */
export const createEmitter = <E extends Record<string, any> = Record<string, unknown>>() =>
  new Emitter<E>();

/** Type alias that matches your previous usage in EventBusContext.tsx */
export type EventBus = ReturnType<typeof createEmitter>;

